
import {
  defineComponent,
  computed,
  onMounted,
  SetupContext,
  toRefs,
  PropType
} from "vue";
import MyListFolderList from "@/components/favorites/MyListFolderList.vue";
import CommonHorizontalRecipeList from "@/components/commonRecipe/CommonHorizontalRecipeList.vue";
import { FavoriteRecipeClip, FolderInfo } from "@/types/favorites";
import NativeClient from "@/client/native-client";
import router from "@/router";
import { createFavoriteRecipesToCommonRecipes } from "@/utils/favorites-util";
import { PAGE_ID } from "@/const/log-const";
import { BeaconLogInfo } from "@/types/common";
import store from "@/store";

export default defineComponent({
  name: "MyList",
  components: { MyListFolderList, CommonHorizontalRecipeList },
  props: {
    /** 最近登録したリスト */
    recipeList: {
      type: Array as PropType<FavoriteRecipeClip[]>,
      default: () => null
    },
    /** フォルダーリスト */
    folderList: {
      type: Array as PropType<FolderInfo[]>,
      default: () => null
    },
  },
  setup(props, context: SetupContext) {
    const { recipeList } = toRefs(props);
    const { folderList } = toRefs(props);
    NativeClient.sendDebugLog("MyList recipeList");

    onMounted(() => {
      //execute()
    });

    // テキスト
    const mylistAllTitle = "最近追加したもの";
    const mylistAllReferenceTitle = "もっと見る";
    const mylistTitle = "フォルダ";
    const mylistEditTitle = "フォルダを編集⋯";
    const nothingMylistMessage = "登録はありません<br>レシピから「マイリストに追加」をする<br>と表示されます。";
    const mylistAppendFolder = "新しいフォルダを追加";
    const nothingFolderMessage = "＋ボタンで追加して10個まで登録できます。";

    // ボタン等のイベント処理
    const selectFolder = (id: number, name: string) => {
      // 対応カテゴリー詳細の画面へ遷移
      NativeClient.sendDebugLog("MyList page transition = /mylist-folder?folderId=" + id + '&folderName=' + name);
      const encodedName = encodeURIComponent(name);
      store.dispatch("favorites/updateFolderName", name);
      router.push(`/mylist-folder?folderId=` + id + '&folderName=' + encodedName);
    };
    const selectRecipe = (recipeId: number, type: string) => {
      // 最近追加したレシピに遷移
      NativeClient.sendDebugLog("MyList recipeId = " + recipeId + ", type = " + type);

      // Beacon Log
      const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, "btn_1_E-08_MylistRecipe", [String(recipeId)]);
      NativeClient.sendClickedActionLog(logInfo);

      NativeClient.transitionReceipDetail(recipeId, type);
    };
    const selectAllList = () => {
      // 最近追加したレシピ一覧へ
      router.push(`/mylist-folder`);
    };
    const selectAppendFolder = () => {
      NativeClient.sendDebugLog("MyList append folder");
      context.emit('append-folder');
    };
    const moveFolder = (folderId: number, position: number) => {
      NativeClient.sendDebugLog("MyList move folder list folderId = " + folderId + ", position = " + position);
      context.emit('move-folder', folderId, position);
    }

    // 表示/非表示設定
    const styleAllListButton = computed(() => {
      NativeClient.sendDebugLog("MyList styleAllListButton");
      if (recipeList.value == null) {
        NativeClient.sendDebugLog("MyList styleAllListButton recipeList null branch");
        return { visibility: `hidden` };
      } else if (recipeList.value.length == 0) {
        return { visibility: `hidden` };
      }
      return { visibility: `visible` };
    });
    const styleAllList = computed(() => {
      if (recipeList.value == null) {
        NativeClient.sendDebugLog("MyList styleAllList hidden");
        return { display: `none` };
      } else if (recipeList.value.length == 0) {
        NativeClient.sendDebugLog("MyList styleAllList recipeList has no element");
        return { display: `none` };
      }
      NativeClient.sendDebugLog("MyList styleAllList normal");
      return { display: `inline-box` };
    });
    const styleAllListNothing = computed(() => {
      if (recipeList.value == null) {
        NativeClient.sendDebugLog("MyList styleAllListNothing hidden");
        return { display: `inline-box`, visibility: `hidden` };
      } else if (recipeList.value.length == 0) {
        NativeClient.sendDebugLog("MyList styleAllListNothing visible");
        return { display: `inline-box`, visibility: `visible` };
      }
      NativeClient.sendDebugLog("MyList styleAllListNothing none");
      return { display: `none` };
    });
    const styleAppendFolder = computed(() => {
      NativeClient.sendDebugLog("MyList styleAppendFolder");
      if (folderList.value == null) {
        NativeClient.sendDebugLog("MyList beforeReceivedFolderList branch");
        return { visibility: 'hidden' };
      }
      return { visibility: `visible` };
    });
    const styleFolderList = computed(() => {
      if (folderList.value == null) {
        return { display: `none` };
      } else if (folderList.value.length == 0) {
        return { display: `none` };
      }
      return { display: `block` };
    });
    const styleFolderListNothing = computed(() => {
      if (folderList.value == null) {
        return { display: `inline-box`, visibility: `hidden` };
      } else if (folderList.value.length == 0) {
        return { display: `inline-box`, visibility: `visible` };
      }
      return { display: `none` };
    });

    const allList = computed(() => {
      // マイリストをFavoritesHistoriesHorizontalRecipeList用のものに切り替える
      if (recipeList.value == null) {
        NativeClient.sendDebugLog("MyList allList recipeList is null");
        return null;
      }
      NativeClient.sendDebugLog("MyList allList recipeList isn't null");
      const allList = createFavoriteRecipesToCommonRecipes(recipeList.value);
      return allList;
    });

    // フォルダ一覧
    const displayFolderList = computed(() => {
      // フォルダ一位欄を変換する
      const displayFolderList: FolderInfo[] = [];
      if (folderList.value == null) {
        return [];
      }
      for (const folderElement of folderList.value) {
        const element = new FolderInfo();
        element.id = folderElement.id;
        element.name = folderElement.name;
        element.image = folderElement.image;
        element.numberOfItems = folderElement.numberOfItems;
        displayFolderList.push(element);
      }
      return displayFolderList
    });

    return {
      mylistAllTitle,
      mylistAllReferenceTitle,
      mylistTitle,
      mylistEditTitle,
      allList,
      displayFolderList,
      selectFolder,
      selectRecipe,
      selectAllList,
      moveFolder,
      nothingMylistMessage,
      nothingFolderMessage,
      mylistAppendFolder,
      selectAppendFolder,
      styleAllListButton,
      styleAllList,
      styleAllListNothing,
      styleAppendFolder,
      styleFolderList,
      styleFolderListNothing
    };
  }
});
